"use strict";

/**
  * @reach/dropdown v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */

// src/reach-dropdown.tsx
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.array.push.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.define-properties.js";
import "core-js/modules/es.object.define-property.js";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["onKeyDown", "onMouseDown", "id", "ref"],
  _excluded2 = ["as"],
  _excluded3 = ["index", "isLink", "onClick", "onDragStart", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseMove", "onMouseUp", "onSelect", "disabled", "onFocus", "valueText", "ref"],
  _excluded4 = ["as"],
  _excluded5 = ["id", "onKeyDown", "ref"],
  _excluded6 = ["as"],
  _excluded7 = ["onBlur", "portal", "position", "ref"],
  _excluded8 = ["as"];
import "core-js/modules/es.array.find-index.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.starts-with.js";
import "core-js/modules/es.array.index-of.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from "react";
import { useId } from "@reach/auto-id";
import { Popover } from "@reach/popover";
import { createDescendantContext, DescendantProvider, useDescendant, useDescendants, useDescendantsInit, useDescendantKeyDown } from "@reach/descendants";
import { composeEventHandlers, createContext, getOwnerDocument, isFunction, isString, isRightClick, useComposedRefs, usePrevious, useStatefulRefValue, makeId } from "@reach/utils";
var CLEAR_SELECTION_INDEX = "CLEAR_SELECTION_INDEX";
var CLICK_MENU_ITEM = "CLICK_MENU_ITEM";
var CLOSE_MENU = "CLOSE_MENU";
var OPEN_MENU_AT_FIRST_ITEM = "OPEN_MENU_AT_FIRST_ITEM";
var OPEN_MENU_AT_INDEX = "OPEN_MENU_AT_INDEX";
var OPEN_MENU_CLEARED = "OPEN_MENU_CLEARED";
var SEARCH_FOR_ITEM = "SEARCH_FOR_ITEM";
var SELECT_ITEM_AT_INDEX = "SELECT_ITEM_AT_INDEX";
var SET_BUTTON_ID = "SET_BUTTON_ID";
var DropdownDescendantContext = createDescendantContext("DropdownDescendantContext");
var _createContext = createContext("Dropdown"),
  _createContext2 = _slicedToArray(_createContext, 2),
  DropdownProvider = _createContext2[0],
  useDropdownContext = _createContext2[1];
var initialState = {
  triggerId: null,
  isExpanded: false,
  typeaheadQuery: "",
  selectionIndex: -1
};
var DropdownProvider_ = function DropdownProvider_(_ref) {
  var id = _ref.id,
    children = _ref.children;
  var triggerRef = React.useRef(null);
  var dropdownRef = React.useRef(null);
  var popoverRef = React.useRef(null);
  var _useDescendantsInit = useDescendantsInit(),
    _useDescendantsInit2 = _slicedToArray(_useDescendantsInit, 2),
    descendants = _useDescendantsInit2[0],
    setDescendants = _useDescendantsInit2[1];
  var _id = useId(id);
  var dropdownId = id || makeId("menu", _id);
  var triggerId = makeId("menu-button", dropdownId);
  var _React$useReducer = React.useReducer(reducer, _objectSpread(_objectSpread({}, initialState), {}, {
      triggerId: triggerId
    })),
    _React$useReducer2 = _slicedToArray(_React$useReducer, 2),
    state = _React$useReducer2[0],
    dispatch = _React$useReducer2[1];
  var triggerClickedRef = React.useRef(false);
  var selectCallbacks = React.useRef([]);
  var readyToSelect = React.useRef(false);
  var mouseDownStartPosRef = React.useRef({
    x: 0,
    y: 0
  });
  React.useEffect(function () {
    if (state.isExpanded) {
      window.__REACH_DISABLE_TOOLTIPS = true;
      window.requestAnimationFrame(function () {
        focus(dropdownRef.current);
      });
    } else {
      window.__REACH_DISABLE_TOOLTIPS = false;
    }
  }, [state.isExpanded]);
  return /* @__PURE__ */React.createElement(DescendantProvider, {
    context: DropdownDescendantContext,
    items: descendants,
    set: setDescendants
  }, /* @__PURE__ */React.createElement(DropdownProvider, {
    dispatch: dispatch,
    dropdownId: dropdownId,
    dropdownRef: dropdownRef,
    mouseDownStartPosRef: mouseDownStartPosRef,
    popoverRef: popoverRef,
    readyToSelect: readyToSelect,
    selectCallbacks: selectCallbacks,
    state: state,
    triggerClickedRef: triggerClickedRef,
    triggerRef: triggerRef
  }, isFunction(children) ? children({
    isExpanded: state.isExpanded,
    isOpen: state.isExpanded
  }) : children));
};
DropdownProvider_.displayName = "DropdownProvider";
function useDropdownTrigger(_ref2) {
  var onKeyDown = _ref2.onKeyDown,
    onMouseDown = _ref2.onMouseDown,
    id = _ref2.id,
    forwardedRef = _ref2.ref,
    props = _objectWithoutProperties(_ref2, _excluded);
  var _useDropdownContext = useDropdownContext("useDropdownTrigger"),
    dispatch = _useDropdownContext.dispatch,
    dropdownId = _useDropdownContext.dropdownId,
    mouseDownStartPosRef = _useDropdownContext.mouseDownStartPosRef,
    triggerClickedRef = _useDropdownContext.triggerClickedRef,
    triggerRef = _useDropdownContext.triggerRef,
    _useDropdownContext$s = _useDropdownContext.state,
    triggerId = _useDropdownContext$s.triggerId,
    isExpanded = _useDropdownContext$s.isExpanded;
  var ref = useComposedRefs(triggerRef, forwardedRef);
  var items = useDropdownDescendants();
  var firstNonDisabledIndex = React.useMemo(function () {
    return items.findIndex(function (item) {
      return !item.disabled;
    });
  }, [items]);
  React.useEffect(function () {
    if (id != null && id !== triggerId) {
      dispatch({
        type: SET_BUTTON_ID,
        payload: id
      });
    }
  }, [triggerId, dispatch, id]);
  React.useEffect(function () {
    if (id == null) {
      dispatch({
        type: SET_BUTTON_ID,
        payload: makeId("menu-button", dropdownId)
      });
    }
  }, [dropdownId, dispatch, id]);
  function handleKeyDown(event) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        dispatch({
          type: OPEN_MENU_AT_INDEX,
          payload: {
            index: firstNonDisabledIndex
          }
        });
        break;
      case "Enter":
      case " ":
        dispatch({
          type: OPEN_MENU_AT_INDEX,
          payload: {
            index: firstNonDisabledIndex
          }
        });
        break;
      default:
        break;
    }
  }
  function handleMouseDown(event) {
    if (isRightClick(event.nativeEvent)) {
      return;
    }
    mouseDownStartPosRef.current = {
      x: event.clientX,
      y: event.clientY
    };
    if (!isExpanded) {
      triggerClickedRef.current = true;
    }
    if (isExpanded) {
      dispatch({
        type: CLOSE_MENU
      });
    } else {
      dispatch({
        type: OPEN_MENU_CLEARED
      });
    }
  }
  return {
    data: {
      isExpanded: isExpanded,
      controls: dropdownId
    },
    props: _objectSpread(_objectSpread({}, props), {}, {
      ref: ref,
      id: triggerId || void 0,
      onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown),
      onMouseDown: composeEventHandlers(onMouseDown, handleMouseDown),
      type: "button"
    })
  };
}
var DropdownTrigger = /*#__PURE__*/React.forwardRef(function (_ref3, forwardedRef) {
  var _ref3$as = _ref3.as,
    Comp = _ref3$as === void 0 ? "button" : _ref3$as,
    rest = _objectWithoutProperties(_ref3, _excluded2);
  var _useDropdownTrigger = useDropdownTrigger(_objectSpread(_objectSpread({}, rest), {}, {
      ref: forwardedRef
    })),
    props = _useDropdownTrigger.props;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread({
    "data-reach-dropdown-trigger": ""
  }, props));
});
DropdownTrigger.displayName = "DropdownTrigger";
function useDropdownItem(_ref4) {
  var indexProp = _ref4.index,
    _ref4$isLink = _ref4.isLink,
    isLink = _ref4$isLink === void 0 ? false : _ref4$isLink,
    onClick = _ref4.onClick,
    onDragStart = _ref4.onDragStart,
    onMouseDown = _ref4.onMouseDown,
    onMouseEnter = _ref4.onMouseEnter,
    onMouseLeave = _ref4.onMouseLeave,
    onMouseMove = _ref4.onMouseMove,
    onMouseUp = _ref4.onMouseUp,
    onSelect = _ref4.onSelect,
    disabled = _ref4.disabled,
    onFocus = _ref4.onFocus,
    valueTextProp = _ref4.valueText,
    forwardedRef = _ref4.ref,
    props = _objectWithoutProperties(_ref4, _excluded3);
  var _useDropdownContext2 = useDropdownContext("useDropdownItem"),
    dispatch = _useDropdownContext2.dispatch,
    dropdownRef = _useDropdownContext2.dropdownRef,
    mouseDownStartPosRef = _useDropdownContext2.mouseDownStartPosRef,
    readyToSelect = _useDropdownContext2.readyToSelect,
    selectCallbacks = _useDropdownContext2.selectCallbacks,
    triggerRef = _useDropdownContext2.triggerRef,
    _useDropdownContext2$ = _useDropdownContext2.state,
    selectionIndex = _useDropdownContext2$.selectionIndex,
    isExpanded = _useDropdownContext2$.isExpanded;
  var ownRef = React.useRef(null);
  var _React$useState = React.useState(valueTextProp || ""),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    valueText = _React$useState2[0],
    setValueText = _React$useState2[1];
  var setValueTextFromDOM = React.useCallback(function (node) {
    if (!valueTextProp && node !== null && node !== void 0 && node.textContent) {
      setValueText(node.textContent);
    }
  }, [valueTextProp]);
  var mouseEventStarted = React.useRef(false);
  var _useStatefulRefValue = useStatefulRefValue(ownRef, null),
    _useStatefulRefValue2 = _slicedToArray(_useStatefulRefValue, 2),
    element = _useStatefulRefValue2[0],
    handleRefSet = _useStatefulRefValue2[1];
  var descendant = React.useMemo(function () {
    return {
      element: element,
      key: valueText,
      disabled: disabled,
      isLink: isLink
    };
  }, [disabled, element, isLink, valueText]);
  var index = useDescendant(descendant, DropdownDescendantContext, indexProp);
  var isSelected = index === selectionIndex && !disabled;
  var ref = useComposedRefs(forwardedRef, handleRefSet, setValueTextFromDOM);
  selectCallbacks.current[index] = onSelect;
  function select() {
    focus(triggerRef.current);
    onSelect && onSelect();
    dispatch({
      type: CLICK_MENU_ITEM
    });
  }
  function handleClick(event) {
    if (isRightClick(event.nativeEvent)) {
      return;
    }
    if (isLink) {
      if (disabled) {
        event.preventDefault();
      } else {
        select();
      }
    }
  }
  function handleDragStart(event) {
    if (isLink) {
      event.preventDefault();
    }
  }
  function handleMouseDown(event) {
    if (isRightClick(event.nativeEvent)) {
      return;
    }
    if (isLink) {
      mouseEventStarted.current = true;
    } else {
      event.preventDefault();
    }
  }
  function handleMouseEnter(event) {
    var doc = getOwnerDocument(dropdownRef.current);
    if (!isSelected && index != null && !disabled) {
      if (dropdownRef !== null && dropdownRef !== void 0 && dropdownRef.current && dropdownRef.current !== doc.activeElement && ownRef.current !== doc.activeElement) {
        dropdownRef.current.focus();
      }
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: index
        }
      });
    }
  }
  function handleMouseLeave(event) {
    dispatch({
      type: CLEAR_SELECTION_INDEX
    });
  }
  function handleMouseMove(event) {
    if (!readyToSelect.current) {
      var threshold = 8;
      var deltaX = Math.abs(event.clientX - mouseDownStartPosRef.current.x);
      var deltaY = Math.abs(event.clientY - mouseDownStartPosRef.current.y);
      if (deltaX > threshold || deltaY > threshold) {
        readyToSelect.current = true;
      }
    }
    if (!isSelected && index != null && !disabled) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: index,
          dropdownRef: dropdownRef
        }
      });
    }
  }
  function handleFocus() {
    readyToSelect.current = true;
    if (!isSelected && index != null && !disabled) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: index
        }
      });
    }
  }
  function handleMouseUp(event) {
    if (isRightClick(event.nativeEvent)) {
      return;
    }
    if (!readyToSelect.current) {
      readyToSelect.current = true;
      return;
    }
    if (isLink) {
      if (mouseEventStarted.current) {
        mouseEventStarted.current = false;
      } else if (ownRef.current) {
        ownRef.current.click();
      }
    } else {
      if (!disabled) {
        select();
      }
    }
  }
  React.useEffect(function () {
    if (isExpanded) {
      var id = window.setTimeout(function () {
        readyToSelect.current = true;
      }, 400);
      return function () {
        window.clearTimeout(id);
      };
    } else {
      readyToSelect.current = false;
    }
  }, [isExpanded, readyToSelect]);
  React.useEffect(function () {
    var ownerDocument = getOwnerDocument(ownRef.current);
    ownerDocument.addEventListener("mouseup", listener);
    return function () {
      ownerDocument.removeEventListener("mouseup", listener);
    };
    function listener() {
      mouseEventStarted.current = false;
    }
  }, []);
  return {
    data: {
      disabled: disabled
    },
    props: _objectSpread(_objectSpread({
      id: useItemId(index),
      tabIndex: -1
    }, props), {}, {
      ref: ref,
      "data-disabled": disabled ? "" : void 0,
      "data-selected": isSelected ? "" : void 0,
      "data-valuetext": valueText,
      onClick: composeEventHandlers(onClick, handleClick),
      onDragStart: composeEventHandlers(onDragStart, handleDragStart),
      onMouseDown: composeEventHandlers(onMouseDown, handleMouseDown),
      onMouseEnter: composeEventHandlers(onMouseEnter, handleMouseEnter),
      onMouseLeave: composeEventHandlers(onMouseLeave, handleMouseLeave),
      onMouseMove: composeEventHandlers(onMouseMove, handleMouseMove),
      onFocus: composeEventHandlers(onFocus, handleFocus),
      onMouseUp: composeEventHandlers(onMouseUp, handleMouseUp)
    })
  };
}
var DropdownItem = /*#__PURE__*/React.forwardRef(function (_ref5, forwardedRef) {
  var _ref5$as = _ref5.as,
    Comp = _ref5$as === void 0 ? "div" : _ref5$as,
    rest = _objectWithoutProperties(_ref5, _excluded4);
  var _useDropdownItem = useDropdownItem(_objectSpread(_objectSpread({}, rest), {}, {
      ref: forwardedRef
    })),
    props = _useDropdownItem.props;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread({
    "data-reach-dropdown-item": ""
  }, props));
});
DropdownItem.displayName = "DropdownItem";
function useDropdownItems(_ref6) {
  var id = _ref6.id,
    onKeyDown = _ref6.onKeyDown,
    forwardedRef = _ref6.ref,
    props = _objectWithoutProperties(_ref6, _excluded5);
  var _useDropdownContext3 = useDropdownContext("useDropdownItems"),
    dispatch = _useDropdownContext3.dispatch,
    triggerRef = _useDropdownContext3.triggerRef,
    dropdownRef = _useDropdownContext3.dropdownRef,
    selectCallbacks = _useDropdownContext3.selectCallbacks,
    dropdownId = _useDropdownContext3.dropdownId,
    _useDropdownContext3$ = _useDropdownContext3.state,
    isExpanded = _useDropdownContext3$.isExpanded,
    triggerId = _useDropdownContext3$.triggerId,
    selectionIndex = _useDropdownContext3$.selectionIndex,
    typeaheadQuery = _useDropdownContext3$.typeaheadQuery;
  var items = useDropdownDescendants();
  var ref = useComposedRefs(dropdownRef, forwardedRef);
  React.useEffect(function () {
    var match = findItemFromTypeahead(items, typeaheadQuery);
    if (typeaheadQuery && match != null) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: match,
          dropdownRef: dropdownRef
        }
      });
    }
    var timeout = window.setTimeout(function () {
      return typeaheadQuery && dispatch({
        type: SEARCH_FOR_ITEM,
        payload: ""
      });
    }, 1e3);
    return function () {
      return window.clearTimeout(timeout);
    };
  }, [dispatch, items, typeaheadQuery, dropdownRef]);
  var prevItemsLength = usePrevious(items.length);
  var prevSelected = usePrevious(items[selectionIndex]);
  var prevSelectionIndex = usePrevious(selectionIndex);
  React.useEffect(function () {
    if (selectionIndex > items.length - 1) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: items.length - 1,
          dropdownRef: dropdownRef
        }
      });
    } else if (prevItemsLength !== items.length && selectionIndex > -1 && prevSelected && prevSelectionIndex === selectionIndex && items[selectionIndex] !== prevSelected) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: items.findIndex(function (i) {
            return i.key === (prevSelected === null || prevSelected === void 0 ? void 0 : prevSelected.key);
          }),
          dropdownRef: dropdownRef
        }
      });
    }
  }, [dropdownRef, dispatch, items, prevItemsLength, prevSelected, prevSelectionIndex, selectionIndex]);
  var handleKeyDown = composeEventHandlers(function handleKeyDown2(event) {
    var key = event.key;
    if (!isExpanded) {
      return;
    }
    switch (key) {
      case "Enter":
      case " ":
        var selected = items.find(function (item) {
          return item.index === selectionIndex;
        });
        if (selected && !selected.disabled) {
          event.preventDefault();
          if (selected.isLink && selected.element) {
            selected.element.click();
          } else {
            focus(triggerRef.current);
            selectCallbacks.current[selected.index] && selectCallbacks.current[selected.index]();
            dispatch({
              type: CLICK_MENU_ITEM
            });
          }
        }
        break;
      case "Escape":
        focus(triggerRef.current);
        dispatch({
          type: CLOSE_MENU
        });
        break;
      case "Tab":
        event.preventDefault();
        break;
      default:
        if (isString(key) && key.length === 1) {
          var query = typeaheadQuery + key.toLowerCase();
          dispatch({
            type: SEARCH_FOR_ITEM,
            payload: query
          });
        }
        break;
    }
  }, useDescendantKeyDown(DropdownDescendantContext, {
    currentIndex: selectionIndex,
    orientation: "vertical",
    rotate: false,
    filter: function filter(item) {
      return !item.disabled;
    },
    callback: function callback(index) {
      dispatch({
        type: SELECT_ITEM_AT_INDEX,
        payload: {
          index: index,
          dropdownRef: dropdownRef
        }
      });
    },
    key: "index"
  }));
  return {
    data: {
      activeDescendant: useItemId(selectionIndex) || void 0,
      triggerId: triggerId
    },
    props: _objectSpread(_objectSpread({
      tabIndex: -1
    }, props), {}, {
      ref: ref,
      id: dropdownId,
      onKeyDown: composeEventHandlers(onKeyDown, handleKeyDown)
    })
  };
}
var DropdownItems = /*#__PURE__*/React.forwardRef(function (_ref7, forwardedRef) {
  var _ref7$as = _ref7.as,
    Comp = _ref7$as === void 0 ? "div" : _ref7$as,
    rest = _objectWithoutProperties(_ref7, _excluded6);
  var _useDropdownItems = useDropdownItems(_objectSpread(_objectSpread({}, rest), {}, {
      ref: forwardedRef
    })),
    props = _useDropdownItems.props;
  return /* @__PURE__ */React.createElement(Comp, _objectSpread({
    "data-reach-dropdown-items": ""
  }, props));
});
DropdownItems.displayName = "DropdownItems";
function useDropdownPopover(_ref8) {
  var onBlur = _ref8.onBlur,
    _ref8$portal = _ref8.portal,
    portal = _ref8$portal === void 0 ? true : _ref8$portal,
    position = _ref8.position,
    forwardedRef = _ref8.ref,
    props = _objectWithoutProperties(_ref8, _excluded7);
  var _useDropdownContext4 = useDropdownContext("useDropdownPopover"),
    triggerRef = _useDropdownContext4.triggerRef,
    triggerClickedRef = _useDropdownContext4.triggerClickedRef,
    dispatch = _useDropdownContext4.dispatch,
    dropdownRef = _useDropdownContext4.dropdownRef,
    popoverRef = _useDropdownContext4.popoverRef,
    isExpanded = _useDropdownContext4.state.isExpanded;
  var ref = useComposedRefs(popoverRef, forwardedRef);
  React.useEffect(function () {
    if (!isExpanded) {
      return;
    }
    var ownerDocument = getOwnerDocument(popoverRef.current);
    function listener(event) {
      if (triggerClickedRef.current) {
        triggerClickedRef.current = false;
      } else if (!popoverContainsEventTarget(popoverRef.current, event.target)) {
        dispatch({
          type: CLOSE_MENU
        });
      }
    }
    ownerDocument.addEventListener("mousedown", listener);
    return function () {
      ownerDocument.removeEventListener("mousedown", listener);
    };
  }, [triggerClickedRef, triggerRef, dispatch, dropdownRef, popoverRef, isExpanded]);
  return {
    data: {
      portal: portal,
      position: position,
      targetRef: triggerRef,
      isExpanded: isExpanded
    },
    props: _objectSpread({
      ref: ref,
      hidden: !isExpanded,
      onBlur: composeEventHandlers(onBlur, function (event) {
        if (event.currentTarget.contains(event.relatedTarget)) {
          return;
        }
        dispatch({
          type: CLOSE_MENU
        });
      })
    }, props)
  };
}
var DropdownPopover = /*#__PURE__*/React.forwardRef(function (_ref9, forwardedRef) {
  var _ref9$as = _ref9.as,
    Comp = _ref9$as === void 0 ? "div" : _ref9$as,
    rest = _objectWithoutProperties(_ref9, _excluded8);
  var _useDropdownPopover = useDropdownPopover(_objectSpread(_objectSpread({}, rest), {}, {
      ref: forwardedRef
    })),
    _useDropdownPopover$d = _useDropdownPopover.data,
    portal = _useDropdownPopover$d.portal,
    targetRef = _useDropdownPopover$d.targetRef,
    position = _useDropdownPopover$d.position,
    props = _useDropdownPopover.props;
  var sharedProps = {
    "data-reach-dropdown-popover": ""
  };
  return portal ? /* @__PURE__ */React.createElement(Popover, _objectSpread(_objectSpread(_objectSpread({}, props), sharedProps), {}, {
    as: Comp,
    targetRef: targetRef,
    position: position,
    unstable_skipInitialPortalRender: true
  })) : /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({}, props), sharedProps));
});
DropdownPopover.displayName = "DropdownPopover";
function findItemFromTypeahead(items) {
  var string = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
  if (!string) {
    return null;
  }
  var found = items.find(function (item) {
    var _item$element, _item$element$dataset, _item$element$dataset2;
    return item.disabled ? false : (_item$element = item.element) === null || _item$element === void 0 ? void 0 : (_item$element$dataset = _item$element.dataset) === null || _item$element$dataset === void 0 ? void 0 : (_item$element$dataset2 = _item$element$dataset.valuetext) === null || _item$element$dataset2 === void 0 ? void 0 : _item$element$dataset2.toLowerCase().startsWith(string);
  });
  return found ? items.indexOf(found) : null;
}
function useItemId(index) {
  var _useDropdownContext5 = useDropdownContext("useItemId"),
    dropdownId = _useDropdownContext5.dropdownId;
  return index != null && index > -1 ? makeId("option-".concat(index), dropdownId) : void 0;
}
function focus(element) {
  element && element.focus();
}
function popoverContainsEventTarget(popover, target) {
  return !!(popover && popover.contains(target));
}
function reducer(state) {
  var action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  switch (action.type) {
    case CLICK_MENU_ITEM:
      return _objectSpread(_objectSpread({}, state), {}, {
        isExpanded: false,
        selectionIndex: -1
      });
    case CLOSE_MENU:
      return _objectSpread(_objectSpread({}, state), {}, {
        isExpanded: false,
        selectionIndex: -1
      });
    case OPEN_MENU_AT_FIRST_ITEM:
      return _objectSpread(_objectSpread({}, state), {}, {
        isExpanded: true,
        selectionIndex: 0
      });
    case OPEN_MENU_AT_INDEX:
      return _objectSpread(_objectSpread({}, state), {}, {
        isExpanded: true,
        selectionIndex: action.payload.index
      });
    case OPEN_MENU_CLEARED:
      return _objectSpread(_objectSpread({}, state), {}, {
        isExpanded: true,
        selectionIndex: -1
      });
    case SELECT_ITEM_AT_INDEX:
      {
        var _action$payload$dropd = action.payload.dropdownRef,
          dropdownRef = _action$payload$dropd === void 0 ? {
            current: null
          } : _action$payload$dropd;
        if (action.payload.index >= 0 && action.payload.index !== state.selectionIndex) {
          if (dropdownRef.current) {
            var doc = getOwnerDocument(dropdownRef.current);
            if (dropdownRef.current !== (doc === null || doc === void 0 ? void 0 : doc.activeElement)) {
              dropdownRef.current.focus();
            }
          }
          return _objectSpread(_objectSpread({}, state), {}, {
            selectionIndex: action.payload.max != null ? Math.min(Math.max(action.payload.index, 0), action.payload.max) : Math.max(action.payload.index, 0)
          });
        }
        return state;
      }
    case CLEAR_SELECTION_INDEX:
      return _objectSpread(_objectSpread({}, state), {}, {
        selectionIndex: -1
      });
    case SET_BUTTON_ID:
      return _objectSpread(_objectSpread({}, state), {}, {
        triggerId: action.payload
      });
    case SEARCH_FOR_ITEM:
      if (typeof action.payload !== "undefined") {
        return _objectSpread(_objectSpread({}, state), {}, {
          typeaheadQuery: action.payload
        });
      }
      return state;
    default:
      return state;
  }
}
function useDropdownDescendants() {
  return useDescendants(DropdownDescendantContext);
}
export { DropdownItem, DropdownItems, DropdownPopover, DropdownProvider_ as DropdownProvider, DropdownTrigger, useDropdownContext, useDropdownDescendants, useDropdownItem, useDropdownItems, useDropdownPopover, useDropdownTrigger };